/**
 * PDV Keyboard Shortcuts Hook
 * Handles F1-F8 keyboard shortcuts for PDV operations.
 * Shortcuts are configurable by admin.
 */
import { useEffect, useCallback } from 'react';

export interface KeyboardShortcut {
  key: string;
  label: string;
  action: () => void;
}

const DEFAULT_SHORTCUTS: Record<string, string> = {
  F1: 'Buscar',
  F2: 'Finalizar',
  F3: 'Pagamento',
  F4: 'Cancelar Item',
  F5: 'Cancelar Venda',
  F6: 'Desconto',
  F7: 'Sangria',
  F8: 'Cliente',
};

interface UsePdvKeyboardOptions {
  onSearch?: () => void;
  onFinalize?: () => void;
  onPayment?: () => void;
  onCancelItem?: () => void;
  onCancelSale?: () => void;
  onDiscount?: () => void;
  onSangria?: () => void;
  onCustomer?: () => void;
  customShortcuts?: Record<string, string>;
  enabled?: boolean;
}

export function usePdvKeyboard(options: UsePdvKeyboardOptions) {
  const {
    onSearch,
    onFinalize,
    onPayment,
    onCancelItem,
    onCancelSale,
    onDiscount,
    onSangria,
    onCustomer,
    customShortcuts,
    enabled = true,
  } = options;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // Don't intercept if user is in a modal input (except the barcode field)
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA') {
        // Never intercept typing in textareas
        if (!e.key.startsWith('F')) return;
      }
      if (
        target.tagName === 'INPUT' &&
        !target.dataset.barcodeInput &&
        target.getAttribute('type') !== 'hidden'
      ) {
        // Allow F-keys even in inputs
        if (!e.key.startsWith('F')) return;
      }

      switch (e.key) {
        case 'F1':
          e.preventDefault();
          onSearch?.();
          break;
        case 'F2':
          e.preventDefault();
          onFinalize?.();
          break;
        case 'F3':
          e.preventDefault();
          onPayment?.();
          break;
        case 'F4':
          e.preventDefault();
          onCancelItem?.();
          break;
        case 'F5':
          e.preventDefault();
          onCancelSale?.();
          break;
        case 'F6':
          e.preventDefault();
          onDiscount?.();
          break;
        case 'F7':
          e.preventDefault();
          onSangria?.();
          break;
        case 'F8':
          e.preventDefault();
          onCustomer?.();
          break;
      }
    },
    [enabled, onSearch, onFinalize, onPayment, onCancelItem, onCancelSale, onDiscount, onSangria, onCustomer],
  );

  useEffect(() => {
    if (!enabled) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);

  // Return shortcut labels for the shortcut bar
  const shortcuts = customShortcuts || DEFAULT_SHORTCUTS;

  const shortcutList: KeyboardShortcut[] = Object.entries(shortcuts).map(([key, label]) => ({
    key,
    label,
    action: () => {
      const event = new KeyboardEvent('keydown', { key });
      handleKeyDown(event);
    },
  }));

  return { shortcuts: shortcutList };
}
